import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AuctionListTable } from '../features/auctions/AuctionListTable';
import { SystemAuctionBrowser } from '../features/auctions/components/SystemAuctionBrowser';
import { useAuctions } from '../features/auctions/hooks/useAuctions';
import { TableProvider } from '../components/shared/table/context/TableContext';
import { Tab } from '@headlessui/react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Auctions() {
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState(location.state?.selectedTab || 0);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Update selected tab when location state changes or when auction param is present
    if (location.state?.selectedTab !== undefined) {
      setSelectedTab(location.state.selectedTab);
    } else if (searchParams.get('auction')) {
      // If auction param is present, switch to Browse Auctions tab
      setSelectedTab(1);
    }
  }, [location.state, searchParams]);
  const { data: auctions, isLoading, error, isFetching } = useAuctions();

  const errorMessage = error?.message || (error instanceof Error ? error.toString() : 'An error occurred');
  const isInitialLoading = isLoading && !auctions;

  return (
    <div className="container mx-auto p-2 sm:p-4 h-full flex flex-col">
      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">Auction Planner</h1>
            {isFetching && !isInitialLoading && (
              <span className="text-sm text-gray-500">(Refreshing...)</span>
            )}
          </div>
          <Tab.List className="flex space-x-1 rounded-xl bg-blue-900/20 p-1 w-full sm:w-auto">
          <Tab
            className={({ selected }) =>
              classNames(
                'w-full sm:w-[150px] rounded-lg py-2.5 text-sm font-medium leading-5',
                'focus:outline-none',
                selected
                  ? 'bg-white shadow text-blue-700'
                  : 'text-white hover:bg-white/[0.15]'
              )
            }
          >
            My Auctions
          </Tab>
          <Tab
            className={({ selected }) =>
              classNames(
                'w-full sm:w-[150px] rounded-lg py-2.5 text-sm font-medium leading-5',
                'focus:outline-none',
                selected
                  ? 'bg-white shadow text-blue-700'
                  : 'text-white hover:bg-white/[0.15]'
              )
            }
          >
            Browse Auctions
          </Tab>
          </Tab.List>
        </div>

        <Tab.Panels className="flex-1 min-h-0">
          <Tab.Panel className="h-full">
            {error && !isInitialLoading && (
              <div className="rounded-md bg-red-50 p-4 my-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">{errorMessage}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow h-full">
              <TableProvider 
                type="auctions"
                initialState={{ 
                  loading: isLoading, 
                  error: errorMessage
                }}
              >
                <AuctionListTable
                  data={auctions || []}
                  loading={isLoading}
                  error={errorMessage}
                />
              </TableProvider>
            </div>
          </Tab.Panel>

          <Tab.Panel className="h-full">
            <SystemAuctionBrowser />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
